/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback, useMemo } from "react";
import { WIDGET_CALLBACK_URL } from "../constants/constants";

const parseUrlParams = (url) => {
  if (!url.includes(WIDGET_CALLBACK_URL)) {
    return null;
  }

  try {
    const partsAfterCallback = url.split(WIDGET_CALLBACK_URL)[1];
    const [pathWithoutQuery, query] = partsAfterCallback.split("?");
    const parts = pathWithoutQuery.split("/");
    // Fields written by buildCallbackUrl are encoded with encodeURIComponent;
    // decode them on read. Numeric fields (location/service/provider) are
    // unaffected — decoding a number is a no-op.
    const decodePart = (raw) => {
      if (raw === undefined) return null;
      try {
        return decodeURIComponent(raw);
      } catch (_) {
        return raw;
      }
    };
    
    const invoiceIdIndex = parts.indexOf("invoice_id");
    const systemIndex = parts.indexOf("system");
    const tokenHash = parts.indexOf("hash");
    const cancelBookingId = pathWithoutQuery.split("/client/cancel-booking/id/")[1]?.split("/")[0];
    const rescheduleBookingId = pathWithoutQuery.split("/client/reschedule-booking/id/")[1]?.split("/")[0];
    // "sheduler_channel" is the backend field name (API contract)
    const schedulerChannel = parts.indexOf("sheduler_channel");
    const location = parts.indexOf("location");
    const service = parts.indexOf("service");
    const provider = parts.indexOf("provider");
    const promotionCode = parts.indexOf("coupon");
    const date = parts.indexOf("date");
    const count = parts.indexOf("count");
    const payLaterInvoiceId = pathWithoutQuery.split("/client/pay-later/id/")[1]?.split("/")[0];
    const onlineMeetingId = pathWithoutQuery.split("/online-meeting/view/id/")[1]?.split("/")[0];
    const isRestorePassword = pathWithoutQuery.includes("restore-password");
    const isOnlineMeeting = pathWithoutQuery.includes("online-meeting");

    const queryParams = new URLSearchParams(query);
    const tokenQuery = queryParams.get("_at");

    return {
      payLaterInvoiceId: payLaterInvoiceId || null,
      invoiceId: invoiceIdIndex !== -1 ? parts[invoiceIdIndex + 1] : null,
      system: systemIndex !== -1 ? parts[systemIndex + 1] : null,
      token: tokenQuery || null,
      hash: tokenHash !== -1 ? parts[tokenHash + 1] : null,
      cancelBookingId: cancelBookingId || null,
      rescheduleBookingId: rescheduleBookingId || null,
      onlineMeetingId: onlineMeetingId || null,
      schedulerChannel: schedulerChannel !== -1 ? +parts[schedulerChannel + 1] : null,
      location: location !== -1 ? decodePart(parts[location + 1]) : null,
      serviceIds:
        service !== -1
          ? decodePart(parts[service + 1] || "").split(",").map((id) => id.trim()).filter(Boolean)
          : [],
      provider: provider !== -1 ? decodePart(parts[provider + 1]) : null,
      promotionCode: promotionCode !== -1 ? decodePart(parts[promotionCode + 1]) : null,
      date: date !== -1 ? decodePart(parts[date + 1]) : null,
      count: count !== -1 ? +parts[count + 1] || null : null,
      isRestorePassword: isRestorePassword || false,
      isOnlineMeeting: isOnlineMeeting || false,
      hasParams: queryParams.size > 0 || pathWithoutQuery.length > 0,
    };
  } catch (error) {
    console.error("Error parsing widget callback URL:", error);
    return null;
  }
};

const useWidgetCallbackParams = () => {
  const initialParams = useMemo(() => {
    return (
      parseUrlParams(window.location.href) || {
        payLaterInvoiceId: null,
        invoiceId: null,
        system: null,
        token: null,
        hash: null,
        cancelBookingId: null,
        rescheduleBookingId: null,
        onlineMeetingId: null,
        schedulerChannel: null,
        location: null,
        serviceIds: [],
        provider: null,
        promotionCode: null,
        date: null,
        count: null,
        isRestorePassword: false,
        hasParams: false,
      }
    );
  }, []);

  const clearParams = useCallback(() => {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);

  return useMemo(
    () => ({
      ...initialParams,
      clearParams,
    }),
    [initialParams, clearParams]
  );
};

export default useWidgetCallbackParams;
